<template>
  <v-container class="confirm-policy-container">
    <v-row no-gutters>
      <v-col cols="12" sm="1">
        <router-link :to="redirectBackLocation" class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" sm="10">
        <div v-if="policyNumber">
          <h1>Summary of your policy</h1>
          <h3>Policy number: {{ policyNumber }}</h3>
        </div>
        <div v-else>
          <h1>Confirm your policy</h1>
          <h3>Check that all the information you submitted is correct</h3>
        </div>
        <v-divider />
      </v-col>
    </v-row>
    <v-row no-gutters class="mt-5">
      <v-col cols="12" md="6" offset-md="1">
        <h2 class="mb-md-3">{{ premiumFrequency }}</h2>
        <h1>R {{ premiumAmount }}</h1>
      </v-col>
      <v-col cols="12" md="5" class="payment-details">
        <h2 class="mb-3">Payment details</h2>
        <p><b>Payment method:</b> Debit Order</p>
        <p><b>Payment date:</b> {{ paymentDate }}</p>
        <p><b>Bank:</b> {{ bankName }}</p>
        <p>
          <b>Account number:</b>
          {{ testator.insurance.bankDetails.accountNumber }}
        </p>
        <p>
          <b>Account holder:</b>
          {{ testator.insurance.bankDetails.accountHolderName }}
        </p>
        <p>
          <b>Branch code:</b> {{ testator.insurance.bankDetails.branchCode }}
        </p>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" md="10" offset-md="1" class="my-5">
        <h2 class="mb-5">Quote based on the following information provided</h2>
        <v-simple-table class="user-view-table">
          <thead>
            <tr>
              <th>Policy Holder</th>
              <th>Age</th>
              <th>Smoker</th>
              <th>Education Level</th>
              <th>Replacement Policy</th>
              <th>Sum insured</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ testator.fullName }}</td>
              <td>{{ testator.age }}</td>
              <td>{{ testator.insurance.smoker }}</td>
              <td>{{ educationLevel }}</td>
              <td>{{ replacementPolicy }}</td>
              <td>R {{ sumInsured | currency }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" md="10" offset-md="1" class="my-5">
        <h2 class="mb-5">Declaration</h2>
        <p>
          I, <b>{{ testator.fullName }}</b
          >, identity number <b>{{ testator.identityNumber.number }}</b
          >, confirm that:
        </p>
        <ul>
          <li>The information in this application is true and correct; and</li>
          <li>
            I have read and agree to abide by the terms set out in the
            <a :href="whitelabel.insuranceTermsPdfLocation" target="_blank"
              >policy document</a
            >; and
          </li>
          <li>
            I am aware of no other information which might be relevant to Old
            Mutual Alternative Risk Transfer's (OMART's) decision to offer me
            cover.
          </li>
          <li>
            I hereby authorise Shackleton Life, on behalf of OMART, to issue a
            payment instruction to collect the monthly premium by debit order
            from my bank account.
          </li>
          <li>
            OMART has outsourced the binder functions to Shackleton Life which
            includes policy administration and claims.
          </li>
        </ul>

        <v-expansion-panels accordion>
          <v-expansion-panel key="0">
            <v-expansion-panel-header hide-actions
              ><a>View full declaration</a></v-expansion-panel-header
            >
            <v-expansion-panel-content>
              <h3 class="mt-5">Full declaration</h3>
              <p>
                I hereby authorise Shackleton Life, on behalf of OMART, to issue
                a payment instruction to collect the monthly premium by debit
                order from my bank account on condition that the sum of such
                payment instruction will never exceed my obligation in terms of
                this application.
              </p>
              <p>
                I acknowledge that all payment instructions issued by Shackleton
                Life, on behalf of OMART shall be treated by my bank as if the
                instruction has been issued by me personally. The debit order
                will be collected every month on the debit order collection date
                selected (as confirmed above). In the event that this collection
                day falls on a Sunday or a recognised South African public
                holiday, the collection day will automatically be the next
                ordinary business day.
              </p>
              <p>
                I acknowledge that this authority may be assigned to a third
                party only if the policy is transferred to another insurer or
                administrator. I understand that the payment instruction will be
                processed through a computerised system provided by the South
                African Banks.
              </p>
              <p>
                I shall not be entitled to any refund of amounts which
                Shackleton Life as the payment distribution agent on behalf of
                OMART has collected while this debit order authority was in
                force if such amounts were legally owing to OMART.
              </p>
              <p>
                This authority may be cancelled by giving Shackleton Life notice
                of not less than 30 days and such cancellations will not
                necessarily cancel my policy.
              </p>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <a target="_blank" :href="whitelabel.termsAndConditionsLocation"
          >View full terms & conditions</a
        >
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <p v-if="policyNumber" class="text-center">
          <b
            >You already have a policy with policy number: {{ policyNumber }}</b
          >
        </p>
        <v-btn
          class="center-insurance-button"
          rounded
          depressed
          large
          color="primary"
          :disabled="policyNumber ? true : false"
          :loading="buttonLoading"
          @click="submit()"
        >
          <span>Accept &amp; take out policy</span>
        </v-btn>
        <v-btn
          v-if="policyNumber"
          class="center-insurance-button"
          rounded
          depressed
          large
          color="primary"
          @click="$router.push({ name: 'OverviewPage' })"
        >
          <span>Next</span><v-icon small>{{ icons.mdiArrowRight }}</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import { educationLevels, banks } from '../../constants/js/insurance';
  import { CREATE_POLICY_MUTATION } from '../../constants/graphql/insurance';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import {
    yearlyPaymentDate,
    monthlyPaymentDate,
  } from '../../constants/js/pro-rata';

  export default {
    name: 'InsurancePolicyConfirmation',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        publicPath: process.env.BASE_URL,
      };
    },
    computed: {
      isAnnualSubscription() {
        return this.testator.insurance.subscriptionType == 'ANNUALLY';
      },
      sumInsured() {
        return this.testator.insurance.rootSumAssured / 100;
      },
      premiumFrequency() {
        return this.isAnnualSubscription ? 'Yearly premium' : 'Monthly premium';
      },
      premiumAmount() {
        return this.isAnnualSubscription
          ? this.testator.insurance.yearlyPremiumQuoted
          : this.testator.insurance.monthlyPremiumQuoted;
      },
      bankName() {
        return banks.find(
          b => b.value == this.testator.insurance.bankDetails.bankName
        ).text;
      },
      educationLevel() {
        return educationLevels.find(
          e => e.value == this.testator.insurance.educationLevel
        ).text;
      },
      replacementPolicy() {
        return this.testator.insurance.replacementPolicy ? 'Yes' : 'No';
      },
      policyNumber() {
        return this.testator.insurance.rootPolicyNumber;
      },
      paymentDate() {
        return this.isAnnualSubscription
          ? yearlyPaymentDate(this.testator.insurance.bankDetails.debitOrderDay)
          : monthlyPaymentDate(
              this.testator.insurance.bankDetails.debitOrderDay
            );
      },
      redirectBackLocation() {
        return this.policyNumber
          ? { name: 'EstimatedInsuranceRequiredOutput' }
          : { name: 'InsuranceBankingDetails' };
      },
    },
    created() {
      // Redirect to start of insurance if there's data missing
      if (!this.testator.insurance || !this.testator.insurance.bankDetails) {
        this.$router.push({ name: 'InsuranceExplanation' });
      }
    },
    methods: {
      submit() {
        this.buttonLoading = true;
        this.createPolicy();
      },
      createPolicy() {
        const insuranceId = this.testator.insurance.id;

        this.$apollo
          .mutate({
            mutation: CREATE_POLICY_MUTATION,
            variables: {
              insuranceId,
            },
            update: (cache, { data: { createPolicy } }) => {
              this.updateCacheAfterCreatePolicy(cache, createPolicy);
            },
          })
          .then(_response => {
            this.$router.push({ name: 'PolicySuccessPage' });
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateCacheAfterCreatePolicy(cache, createPolicy) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        data.testator.insurance = createPolicy;

        cache.writeQuery({ ...query, data: data });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .confirm-policy-container {
    .payment-details {
      p {
        padding-top: 0;
        padding-bottom: 0;
        margin-bottom: 0;
      }
    }

    .v-expansion-panel:before {
      box-shadow: none !important;
    }

    .v-expansion-panel {
      background-color: inherit;
    }

    .v-expansion-panel-content {
      border: 1px solid rgba(0, 0, 0, 0.125);
      border-radius: 5px;
    }
  }
</style>
